/**
 * Badge helpers for the NCR Edit workflow tab row. The live one is
 * `plateBadgeFor` (the server's process plate, QUALITY-NCR-REVIEW T8); the
 * three per-process status helpers below it are DORMANT. Extracted from
 * NCREdit.tsx to keep that page under the 500-line limit.
 */
import type { NonConformanceReport } from '../../lib/api';
import type { PlateKey } from '../../components/quality/ncrPlate';

export type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

export interface TabBadge {
  label: string;
  variant: BadgeVariant;
}


/**
 * T9 (QUALITY-NCR-REVIEW, 30 Sep 2026): a tab's badge is the server's own
 * process label -- the one the grid shows -- so the 8D tab gets one too and a
 * tab can never disagree with the register.
 *
 * @param ncr - the NCR the page shows
 * @param key - which process
 * @returns the badge, or null before the server has a plate (draft preview)
 */
export function plateBadgeFor(ncr: NonConformanceReport, key: PlateKey): TabBadge | null {
  const cell = ncr.process_plate?.[key];
  if (!cell) return null;
  if (cell.done) return { label: cell.label, variant: 'default' };
  return { label: cell.label, variant: cell.owner ? 'secondary' : 'outline' };
}

// DORMANT -- not wired since T9 (30 Sep 2026): the tab row reads plateBadgeFor;
// see QUALITY-NCR-REVIEW. Kept per the retire-don't-delete rule.
/** Collection sub-process status → tab badge. */
export function collectionBadgeFor(ncr: NonConformanceReport): TabBadge {
  if (ncr.collection_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.collection_process?.status;
  // Spec flow (24 Sep 2026): supplier proposes -> customer confirms
  // collection -> supplier confirms receipt.
  if (!status) return { label: 'Awaiting Proposal', variant: 'secondary' };
  switch (status) {
    case 'requested':
    case 'pending':   return { label: 'Awaiting Proposal', variant: 'secondary' };
    // E15: the supplier has proposed a date; the customer hasn't confirmed anything yet.
    case 'confirmed': return { label: 'Proposed', variant: 'outline' };
    case 'collected': return { label: 'Collected', variant: 'outline' };
    case 'completed': return { label: 'Done', variant: 'default' };
    default:          return { label: 'Pending', variant: 'secondary' };
  }
}

// DORMANT -- not wired since T9 (30 Sep 2026); see QUALITY-NCR-REVIEW.
/** Credit sub-process status → tab badge. */
export function creditBadgeFor(ncr: NonConformanceReport): TabBadge {
  if (ncr.credit_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.credit_process?.status;
  // Spec flow (24 Sep 2026): supplier enters the note -> customer confirms receipt.
  // E2/E7 (29 Sep 2026): credit runs alongside collection, so it never waits
  // for the goods (backend process_plate agrees).
  if (!status) return { label: 'Awaiting Note', variant: 'secondary' };
  switch (status) {
    case 'requested':    return { label: 'Awaiting Note', variant: 'secondary' };
    case 'note_submitted': return { label: 'Note Submitted', variant: 'outline' };
    case 'under_review': return { label: 'Under Review', variant: 'secondary' };
    case 'approved':     return { label: 'Approved', variant: 'outline' };
    case 'rejected':     return { label: 'Rejected', variant: 'destructive' };
    case 'processed':    return { label: 'Done', variant: 'default' };
    default:             return { label: 'Pending', variant: 'secondary' };
  }
}

// DORMANT -- not wired since T9 (30 Sep 2026); see QUALITY-NCR-REVIEW.
/**
 * Replacement sub-process status → tab badge — mirrors creditBadgeFor
 * (a claim is resolved by either a credit or a replacement).
 */
export function replacementBadgeFor(ncr: NonConformanceReport): TabBadge {
  if (ncr.replacement_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.replacement_process?.status;
  if (!status) return { label: 'Not Started', variant: 'secondary' };
  switch (status) {
    case 'requested':  return { label: 'Requested', variant: 'secondary' };
    case 'dispatched': return { label: 'Dispatched', variant: 'outline' };
    case 'approved':   return { label: 'Approved', variant: 'outline' };
    case 'rejected':   return { label: 'Rejected', variant: 'destructive' };
    case 'completed':  return { label: 'Done', variant: 'default' };
    default:           return { label: 'Pending', variant: 'secondary' };
  }
}
